import React, { useState, useEffect } from 'react';
import { contactInfo, faqData } from '../mockData';
import { MapPin, Mail, Phone, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [faqs, setFaqs] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });

  useEffect(() => {
    fetchFaqs();
  }, []);

  const fetchFaqs = async () => {
    try {
      const response = await axios.get(`${API}/faq`);
      setFaqs(response.data);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      setFaqs(faqData); // Fallback to mock data
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      
      toast({
        title: "Message Sent!",
        description: "Thank you for contacting us. We'll get back to you within 24-48 hours.",
        duration: 5000
      });
      
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast({
        title: "Submission Failed",
        description: error.response?.data?.detail || "There was an error sending your message. Please try again or email us directly.",
        variant: "destructive",
        duration: 5000
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Get in Touch
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Let's discuss how we can help manufacture and scale your food brand
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-none shadow-xl p-8 md:p-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Send Us a Message</h2>
              <p className="text-gray-600 mb-8">
                Fill out the form and our team will get back to you within 24-48 hours
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Full Name *</label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full rounded-none border-gray-300"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Email Address *</label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full rounded-none border-gray-300"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Phone Number</label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full rounded-none border-gray-300"
                    placeholder="+44 7XXX XXXXXX"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Company/Brand Name</label>
                  <Input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full rounded-none border-gray-300"
                    placeholder="Your Brand"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Message *</label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="w-full rounded-none border-gray-300"
                    placeholder="Tell us about your project, recipe, or manufacturing needs..."
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 rounded-none font-bold text-lg transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <Send size={20} />
                  {submitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Contact Information</h2>
                <p className="text-gray-600 mb-8">
                  Reach out to us directly through any of the channels below
                </p>
              </div>

              {/* Production Facility */}
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 p-8 rounded-none border-l-4 border-emerald-600">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <MapPin className="text-emerald-600" size={24} />
                  Production Facility
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {contactInfo.kitchen.name}<br />
                  {contactInfo.kitchen.address}<br />
                  {contactInfo.kitchen.city}<br />
                  {contactInfo.kitchen.country}
                </p>
              </div>

              {/* Head Office */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-8 rounded-none border-l-4 border-blue-600">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <MapPin className="text-blue-600" size={24} />
                  Head Office
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {contactInfo.office.name}<br />
                  {contactInfo.office.address}<br />
                  {contactInfo.office.city}<br />
                  {contactInfo.office.country}
                </p>
              </div>

              {/* Contact Details */}
              <div className="bg-gray-50 p-8 rounded-none border-l-4 border-gray-600">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Direct Contact</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Mail className="text-emerald-600" size={20} />
                    <a href={`mailto:${contactInfo.company.email}`} className="text-gray-700 hover:text-emerald-600 transition-colors duration-300">
                      {contactInfo.company.email}
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="text-emerald-600" size={20} />
                    <span className="text-gray-700">{contactInfo.company.phone}</span>
                  </div>
                </div>
              </div>

              {/* Company Info */}
              <div className="bg-white p-8 rounded-none shadow-lg border-t-4 border-emerald-600">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{contactInfo.company.name}</h3>
                <p className="text-gray-600">{contactInfo.company.registration}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Quick answers to common questions about our services
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-white rounded-none shadow-md border-l-4 border-emerald-600 px-6"
              >
                <AccordionTrigger className="text-lg font-semibold text-gray-900 hover:text-emerald-600 py-6">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 leading-relaxed pb-6">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">Still have questions?</p>
            <a
              href={`mailto:${contactInfo.company.email}`}
              className="inline-block bg-emerald-600 text-white px-8 py-4 rounded-none font-semibold transition-all duration-300 hover:bg-emerald-700 hover:shadow-xl hover:-translate-y-1"
            >
              Email Us Directly
            </a>
          </div>
        </div>
      </section>

      {/* Map or Additional CTA */}
      <section className="py-20 bg-emerald-600">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Visit Our Facilities
          </h2>
          <p className="text-xl text-emerald-100 mb-8 max-w-3xl mx-auto">
            Schedule a tour of our Birmingham production facility to see how we work
          </p>
          <a
            href={`mailto:${contactInfo.company.email}?subject=Facility Tour Request`}
            className="inline-block bg-white text-emerald-600 px-10 py-5 rounded-none font-bold text-lg transition-all duration-300 hover:bg-gray-100 hover:shadow-2xl hover:-translate-y-1"
          >
            Book a Facility Tour
          </a>
        </div>
      </section>
    </div>
  );
};

export default Contact;
